import { useState, type FormEvent } from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

interface FinalCTASectionProps {
  headline?: string;
  subheadline?: string;
  backgroundImage?: string;
}

const FinalCTASection = ({
  headline = 'Ready to Elevate Your Space?',
  subheadline = 'Contact Polished Concrete Plus today for a free consultation and estimate. We serve Minnesota and surrounding states.',
  backgroundImage = 'http://www.polishedconcreteplus.com/images/pic1.jpg',
}: FinalCTASectionProps) => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const { ref, isVisible } = useScrollAnimation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const name = data.get('name')?.toString().trim();
    const phone = data.get('phone')?.toString().trim();
    const email = data.get('email')?.toString().trim();
    const service = data.get('service')?.toString().trim();
    const message = data.get('message')?.toString().trim();

    if (!name || !phone || !email) {
      setStatus('error');
      return;
    }

    setStatus('sending');

    const body = new URLSearchParams();
    body.append('name', name);
    body.append('phone', phone);
    body.append('email', email ?? '');
    body.append('service', service ?? '');
    body.append('message', message ?? '');

    try {
      const res = await fetch('https://readdy.ai/api/form/d71uics991ove783t78g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={backgroundImage} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[#0a0a0a]/88" />
      </div>

      <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 py-24 lg:py-32`}>
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: CTA copy */}
          <div className="lg:w-[45%]">
            <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-5">Get Started</p>
            <h2
              className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {headline}
            </h2>
            <p className="text-white/55 text-base leading-relaxed mb-10">{subheadline}</p>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#c8a951]/40 shrink-0">
                  <i className="ri-phone-line text-[#c8a951]" />
                </div>
                <div>
                  <p className="text-white/35 text-xs uppercase tracking-[0.15em]">Call Directly</p>
                  <a href="tel:3204931454" className="text-white font-semibold hover:text-[#c8a951] transition-colors">
                    320.493.1454
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#c8a951]/40 shrink-0">
                  <i className="ri-mail-line text-[#c8a951]" />
                </div>
                <div>
                  <p className="text-white/35 text-xs uppercase tracking-[0.15em]">Email Us</p>
                  <a href="mailto:josh@polishedconcreteplus.com" className="text-white font-semibold hover:text-[#c8a951] transition-colors text-sm">
                    josh@polishedconcreteplus.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#c8a951]/40 shrink-0">
                  <i className="ri-map-pin-2-line text-[#c8a951]" />
                </div>
                <div>
                  <p className="text-white/35 text-xs uppercase tracking-[0.15em]">Our Address</p>
                  <p className="text-white font-semibold text-sm">15076 15th Ave NW, Rice, MN 56367</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:w-[55%] w-full">
            <div className="border border-[#c8a951]/20 p-8 lg:p-10 bg-[#0a0a0a]/60">
              <h3
                className="font-display text-2xl font-bold text-white mb-7"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Request a Free Estimate
              </h3>

              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center border border-[#c8a951] mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-[#c8a951]" />
                  </div>
                  <p className="text-white font-semibold text-lg mb-2">Message Received!</p>
                  <p className="text-white/50 text-sm">We'll be in touch shortly to discuss your project.</p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-[0.18em] mb-2">Full Name *</label>
                      <input
                        name="name"
                        required
                        placeholder="Your full name"
                        className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 outline-none focus:border-[#c8a951] transition-colors placeholder:text-white/25"
                      />
                    </div>
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-[0.18em] mb-2">Phone *</label>
                      <input
                        name="phone"
                        required
                        placeholder="Your phone number"
                        className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 outline-none focus:border-[#c8a951] transition-colors placeholder:text-white/25"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-[0.18em] mb-2">Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 outline-none focus:border-[#c8a951] transition-colors placeholder:text-white/25"
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-[0.18em] mb-2">Service Needed</label>
                    <select
                      name="service"
                      className="w-full bg-[#0a0a0a] border border-white/20 text-white text-sm px-4 py-3 outline-none focus:border-[#c8a951] transition-colors cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option>Polished Concrete</option>
                      <option>Concrete Restoration</option>
                      <option>Floor Coatings</option>
                      <option>Custom Graphics &amp; Logos</option>
                      <option>Terrazzo</option>
                      <option>Resurfacing</option>
                      <option>Other / Not Sure</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-[0.18em] mb-2">Project Description</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your project, location, and timeline..."
                      className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 outline-none focus:border-[#c8a951] transition-colors placeholder:text-white/25 resize-none"
                    />
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-xs">Please fill in all required fields and try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full bg-[#c8a951] text-[#0a0a0a] text-xs font-bold py-4 uppercase tracking-[0.18em] hover:bg-[#e0c070] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {status === 'sending' ? 'Sending...' : 'Request Free Estimate'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
